/** @jsx jsx */
import { jsx, css } from '@emotion/core'
import React, { useState, useRef, useMemo } from 'react'
import { Modal, Tabs, Spin, message } from 'antd'
import UserDetailsForm, { PasswordResetForm } from './userDetailsForm'
import RoleTypeSelection from './RoleSelectionPanel'
import { connect } from 'react-redux'
import { getPermitActions, getEnvConfig, hasAccesPermits } from 'core'

import * as ScreenPermissions from 'core/EventActions'
import { getIsPasswordDisabled } from 'utils'

const DEFAULT_TAB = 'details'
const PASSWORD_RESET_SUCCESS_MSG = 'Password Reset Successful'

const roleToDisplay = role => role.displayName

const roleToId = role => role.id

const roleAcceptFilter = role => filterText =>
  role.displayName.toLowerCase().includes(filterText.toLowerCase())

const getUserName = user =>
  user.firstName != null
    ? `${user.firstName} ${user.lastName || ''} (${user.userName})`
    : user.userName

const UserModal = ({
  hide,
  user = {},
  userList = [],
  updateTask,
  createTask,
  isLoading,
  allRoles,
  groups,
  companyNameMap,
  stationList,
  setError,
  setSpinning,
  spinning,
  stationMap,
  isLdapUsernameEnabled = false,
  ...props
}) => {
  const hasCopyAddFunctinality = useMemo(
    _ =>
      props.envConfig.authGodMode ||
      hasAccesPermits({
        authGodMode: props.envConfig.authGodMode,
        actionPermits: props.actionPermits,
        eventActions: [ScreenPermissions.USER_ROLE_COPY_NAME_ADD],
        matchAny: true,
      }),
    [props.actionPermits, props.envConfig.authGodMode]
  )

  const hideCompanyStationUpdateModal = useMemo(
    _ => {
      return hasAccesPermits({
        authGodMode: props.envConfig.authGodMode,
        actionPermits: props.actionPermits,
        eventActions: [ScreenPermissions.USER_SCREEN_HIDE_UPDATE_TAB],
        matchAny: true,
      })
    },
    [props.actionPermits, props.envConfig.authGodMode]
  )

  const processing = isLoading || false
  const { isNew = false } = user
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB)
  const [baseData, setBaseData] = useState(
    user.userType
      ? { userStatus: 'ACTIVE', ...user }
      : { userStatus: 'ACTIVE', ...user, userType: 'USER' }
  )
  const userFormRef = useRef()
  // const [err, setErr] = useState()

  // const setterFunc = id => (ids = []) =>
  //   ids.includes(id) ? ids.filter(i => i !== id) : [id, ...ids]

  const setSelectedRoles = setter =>
    setBaseData(r => ({ ...r, roles: setter(r.roles || []) }))
  const selectedRoles = baseData.roles || []

  const onOk = () => {
    userFormRef.current &&
      userFormRef.current.validateFields(error => {
        if (error) {
          setActiveTab(DEFAULT_TAB)
        } else {
          setSpinning(true)
          if (isNew) {
            createTask({
              isNew: false,
              companies: [],
              roles: [],
              groups: [],
              stations: [],
              userStatus: 'ACTIVE',
              failedAttempts: 0,
              sessionStatus: 0,
              ...baseData,
            })
              .map(d => {
                setSpinning(false)
                return d
              })
              .fork(setError, hide)
          } else {
            updateTask(baseData)
              .map(d => {
                setSpinning(false)
                return d
              })
              .fork(setError, hide)
          }
        }
      })
  }

  const onTabChanged = activeKey => {
    setActiveTab(activeKey)
  }

  const passwordDisabled = useMemo(
    _ => getIsPasswordDisabled(props.envConfig),
    [props.envConfig]
  )

  return (
    <Modal
      title={isNew ? 'Create New User' : `Update -  ${getUserName(user)} `}
      width={hasCopyAddFunctinality ? '90vw' : '60vw'}
      visible
      onOk={onOk}
      okText="Save"
      onCancel={hide}
      centered
      bodyStyle={{ height: 650 }}
      maskClosable={false}
      okButtonProps={{ loading: processing }}
      cancelButtonProps={{ loading: processing }}
    >
      <Spin spinning={spinning}>
        <Tabs activeKey={activeTab} onChange={onTabChanged}>
          <Tabs.TabPane key="details" tab="Details">
            <UserDetailsForm
              isNew={isNew}
              user={baseData}
              setUser={setBaseData}
              existingUserNameList={[]}
              userFormRef={userFormRef}
              userNameList={userList.map(user => user.userName)}
              groups={groups}
              companyNameMap={companyNameMap}
              stationList={stationList}
              passwordDisabled={passwordDisabled}
              isLdapUsernameEnabled={isLdapUsernameEnabled}
            />
          </Tabs.TabPane>

          {!hideCompanyStationUpdateModal && (
            <Tabs.TabPane
              key="companyrolestype"
              tab="Company Roles"
              // disabled={baseData.userType === 'ADMIN'}
            >
              <RoleTypeSelection
                data={allRoles.filter(r => r.shifts === 'COMPANY')}
                selectedIds={selectedRoles}
                setSelectedIds={setSelectedRoles}
                toDisplay={roleToDisplay}
                toId={roleToId}
                acceptFilter={roleAcceptFilter}
                height={550 - 100}
                idNameMap={companyNameMap}
                hasCopyAddFunctinality={hasCopyAddFunctinality}
              />
            </Tabs.TabPane>
          )}
          {!hideCompanyStationUpdateModal && (
            <Tabs.TabPane
              key="stationrolestype"
              tab="Station Roles"
              // disabled={baseData.userType === 'ADMIN'}
            >
              <RoleTypeSelection
                data={allRoles.filter(r => r.shifts === 'STATION')}
                selectedIds={selectedRoles}
                setSelectedIds={setSelectedRoles}
                toDisplay={roleToDisplay}
                toId={roleToId}
                acceptFilter={roleAcceptFilter}
                height={550 - 100}
                idNameMap={stationMap}
                resourceType="station"
                hasCopyAddFunctinality={hasCopyAddFunctinality}
              />
            </Tabs.TabPane>
          )}

          {/* <Tabs.TabPane
            key="companyroles"
            tab="Company Roles"
            // disabled={baseData.userType === 'ADMIN'}
          >
            <FlexWrapMultiSelectPane
              data={allRoles.filter(r => r.shifts === 'COMPANY')}
              selectedIds={selectedRoles}
              setSelectedIds={setSelectedRoles}
              toDisplay={roleToDisplay}
              toId={roleToId}
              acceptFilter={roleAcceptFilter}
            />
          </Tabs.TabPane>
          <Tabs.TabPane
            key="stationroles"
            tab="Station Roles"
            // disabled={baseData.userType === 'ADMIN'}
          >
            <FlexWrapMultiSelectPane
              data={allRoles.filter(r => r.shifts !== 'COMPANY')}
              selectedIds={selectedRoles}
              setSelectedIds={setSelectedRoles}
              toDisplay={roleToDisplay}
              toId={roleToId}
              acceptFilter={roleAcceptFilter}
            /> 
          </Tabs.TabPane> */}
        </Tabs>
      </Spin>
    </Modal>
  )
}

const mapStateToProps = state => ({
  actionPermits: getPermitActions(state),
  envConfig: getEnvConfig(state),
})

export default connect(mapStateToProps)(UserModal)

export const PasswordResetModal = ({
  hide,
  user = {},
  resetPassword,
  setError,
}) => {
  const [processing, setProcessing] = useState(false)
  const passwordResetFormRef = useRef()

  const [password, setPassword] = useState('')

  const onOk = () => {
    passwordResetFormRef.current &&
      passwordResetFormRef.current.validateFields(error => {
        if (!error) {
          setProcessing(true)
          resetPassword([
            {
              ...user,
              password,
            },
          ]).fork(
            _ => {
              setProcessing(false)
              setError(error)
            },
            () => {
              setProcessing(false)
              message.success(PASSWORD_RESET_SUCCESS_MSG)
              hide()
            }
          )
        }
      })
  }
  return (
    <Modal
      title="Reset Password"
      width="30vw"
      visible
      onOk={onOk}
      okText="Reset"
      onCancel={hide}
      centered
      maskClosable={false}
      okButtonProps={{ loading: processing }}
      cancelButtonProps={{ loading: processing }}
    >
      <Spin spinning={processing}>
        <PasswordResetForm
          setPassword={setPassword}
          passwordResetFormRef={passwordResetFormRef}
        />
      </Spin>
    </Modal>
  )
}
